import React, { useState } from "react";
import { css } from "@emotion/css";
import type { SessionScope } from "../../types/state";

/**
 * Breadcrumb for nested deep-dives.  Walks the ``parentScopeId`` chain
 * from the active scope up to the lesson root and renders it as
 * tappable crumbs (Lesson › Recursion › Call stack).  Collapses the
 * middle to "…" when deep so it stays one line on mobile; tapping "…"
 * expands the full trail.  Tapping any ancestor navigates there.
 */

const ROOT_LABEL = "Lesson";
const COLLAPSE_THRESHOLD = 3; // show all when chain length ≤ this

const barStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
  background: var(--color-bg-subtle);
  border-bottom: 1px solid var(--color-border-subtle);
  font-size: 0.85em;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const crumbStyle = css`
  border: none;
  background: transparent;
  color: var(--color-primary);
  font-size: inherit;
  font-weight: 600;
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 6px;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover { background: var(--color-primary-muted); }
`;

const currentStyle = css`
  color: var(--color-text);
  font-weight: 700;
  padding: 3px 6px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const sepStyle = css`
  color: var(--color-text-muted);
  user-select: none;
  flex-shrink: 0;
`;

const ellipsisStyle = css`
  ${crumbStyle};
  color: var(--color-text-muted);
`;

interface Crumb {
  id: string;
  label: string;
}

interface ScopeBreadcrumbProps {
  scopes: Record<string, SessionScope>;
  activeScopeId: string;
  onNavigate: (scopeId: string) => void;
}

function buildChain(
  scopes: Record<string, SessionScope>,
  activeScopeId: string,
): Crumb[] {
  const chain: Crumb[] = [];
  let cur: SessionScope | undefined = scopes[activeScopeId];
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    const isRoot = !cur.parentScopeId;
    chain.unshift({ id: cur.id, label: isRoot ? ROOT_LABEL : cur.label });
    cur = cur.parentScopeId ? scopes[cur.parentScopeId] : undefined;
  }
  return chain;
}

export const ScopeBreadcrumb: React.FC<ScopeBreadcrumbProps> = ({
  scopes,
  activeScopeId,
  onNavigate,
}) => {
  const [expanded, setExpanded] = useState(false);
  const chain = buildChain(scopes, activeScopeId);
  if (chain.length <= 1) return null;

  // Collapse the middle when deep (keep first + last two), unless expanded.
  const collapsed =
    !expanded && chain.length > COLLAPSE_THRESHOLD
      ? [chain[0], { id: "__ellipsis__", label: "…" }, chain[chain.length - 2], chain[chain.length - 1]]
      : chain;

  return (
    <nav className={barStyle} aria-label="Breadcrumb" data-testid="scope-breadcrumb">
      {collapsed.map((crumb, idx) => {
        const isLast = idx === collapsed.length - 1;
        const sep = idx > 0 ? <span className={sepStyle} aria-hidden="true">›</span> : null;

        if (crumb.id === "__ellipsis__") {
          return (
            <React.Fragment key="ellipsis">
              {sep}
              <button
                type="button"
                className={ellipsisStyle}
                onClick={() => setExpanded(true)}
                aria-label="Show full path"
                data-testid="breadcrumb-expand"
              >
                …
              </button>
            </React.Fragment>
          );
        }

        if (isLast) {
          return (
            <React.Fragment key={crumb.id}>
              {sep}
              <span className={currentStyle} aria-current="page" data-testid="breadcrumb-current">
                {crumb.label}
              </span>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={crumb.id}>
            {sep}
            <button
              type="button"
              className={crumbStyle}
              onClick={() => onNavigate(crumb.id)}
              data-testid={`breadcrumb-crumb-${idx}`}
            >
              {crumb.label}
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
};
